import React, { useState, useRef, useEffect } from "react";
import { Result, Spin, Table, message } from "antd";
import { useDispatch } from "react-redux";
import get from "lodash/get";
import find from "lodash/find";
import has from "lodash/has";
import { fetchCompanies } from "../../redux/actions/companyActions";
import OfflineCard from "../../components/card/OfflineCard";
import CardSection from "./CardSection";
import TableSection from "./TableSection";

import {
  fetchDashTemps,
  fetchTemplateStats,
  fetchNotificationStats,
} from "../../redux/actions/dashboardActions";
import { markNotificationRead } from "../../redux/actions/notificationActions";
import {
  FullscreenButton,
  POLLING_INTERVAL_MS,
  SubTitle,
  themeClasses,
  Title,
} from "../../constants/andOnDashBoard";
import HeaderSelector from "./HeaderSelector";
import { checkNullable } from "../../utilities/helpers";
import { playNotificationSound } from "../../utilities/dashboard";

function AndonDashboard({ theme = "darkGray" }) {
  const dispatch = useDispatch();
  const currentTheme = themeClasses[theme];
  const [selectedSegment, setSelectedSegment] = useState("cards");
  const [showEmpty, setShowEmpty] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateStats, setTemplateStats] = useState([]);
  const [notificationStats, setNotificationStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const fullscreenRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [filter, setFilters] = useState({
    company: undefined,
  });
 
  const fetchTemplates = (query) => {
    const templateQuery = query
      ? query
      : { company_uuid: get(filter, "company.id") };
    setLoading(true);
    dispatch(fetchDashTemps(templateQuery))
      .then((result) => {
        if (get(result, "length") === 0) {
          setShowEmpty(true);
          setLoading(false);
        }
        setTemplates(result);
        setShowEmpty(false);
        setLoading(false);
      })
      .catch((error) => {
        setShowEmpty(false);
        setLoading(false);
      });
  };

  const playNotification = (result) => {
    const notficaitionExists = find(result, (obj) =>
      has(obj, "latest_notfication"),
    );
    if (notficaitionExists) {
      playNotificationSound();
    }
  };

  const getNotificationStats = (query) => {
    dispatch(fetchNotificationStats(query))
      .then((result) => {
        setNotificationStats(result);
        playNotification(result)
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const fetchStats = (query) => {
    const statsQuery = query
      ? query
      : { company_uuid: get(filter, "company.id") };
    dispatch(fetchTemplateStats(statsQuery))
      .then((result) => {
        if (result) setTemplateStats(result);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const fetchTemplatesAndStats = (query) => {
    fetchTemplates(query);
    fetchStats(query);
    setSelectedSegment("cards");
  };

  const handleFullScreenToggle = () => {
    const element = fullscreenRef.current;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (element?.requestFullscreen) {
      element.requestFullscreen();
    }
  };

  const updateFullScreenState = () => {
    const isFS = document.fullscreenElement;

    setIsFullScreen(isFS);
  };

  const setinitialCompany = (company) => {
    const query = { company_uuid: get(company, "id") };
    fetchTemplatesAndStats(query);

    setFilters({
      ...filter,
      company,
    });
    setConfig({
      ...config,
      companies: [company],
    });
    form.setFieldsValue({
      Company: get(company, "id"),
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (get(result, "length") === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({
            companies: [
              { name: "All", id: "", logo: { key: "", url: "" } },
              ...result,
            ],
          });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const acknowledgeNotification = (id) => {
    dispatch(markNotificationRead(id))
      .then(() => {
        message.success("Notification acknowledged");
      })
      .catch(() => {});
  };

  useEffect(() => {
    getCompanies();
    document.addEventListener("fullscreenchange", updateFullScreenState);
    return () => {
      document.removeEventListener("fullscreenchange", updateFullScreenState);
    };
  }, []);

  useEffect(() => {
    if (get(filter, "company.id")) {
      fetchStats({ company_uuid: get(filter, "company.id") });
      getNotificationStats({ company_uuid: get(filter, "company.id") });
    }
    const polling = setInterval(() => {
      if (get(filter, "company.id")) {
        fetchStats({ company_uuid: get(filter, "company.id") });
        getNotificationStats({ company_uuid: get(filter, "company.id") });
      }
    }, POLLING_INTERVAL_MS);
    return () => {
      clearInterval(polling);
    };
  }, [filter]);

  return (
    <Spin spinning={loading}>
      <div
        className={`box-border font-inter ${get(
          currentTheme,
          "bgPrimary",
        )} min-h-screen`}
      >
        {!isFullScreen && (
          <FullscreenButton
            isFullScreenMode={false}
            onToggle={handleFullScreenToggle}
          />
        )}
        <div
          ref={fullscreenRef}
          className={`${
            isFullScreen
              ? `fixed inset-0 z-50 p-2 ${get(
                  currentTheme,
                  "bgFullScreen",
                )} flex flex-col h-screen`
              : "relative p-2"
          }`}
        >
          <HeaderSelector
            filter={filter}
            currentTheme={currentTheme}
            selectedSegment={selectedSegment}
            config={config}
            setFilters={setFilters}
            onChange={fetchTemplatesAndStats}
            value={checkNullable}
            stats={templateStats}
            setSelectedSegment={setSelectedSegment}
          />
          {isFullScreen && (
            <FullscreenButton
              isFullScreenMode={true}
              onToggle={handleFullScreenToggle}
            />
          )}
          <div
            className={`flex-grow rounded-lg ${get(
              currentTheme,
              "cardShadow",
            )} flex flex-col overflow-hidden`}
          >
            <div className="flex-grow overflow-y-auto p-2">
              {selectedSegment === "cards" && (
                <CardSection
                  templates={templates}
                  templateStats={templateStats}
                  notificationStats={notificationStats}
                  isFullScreen={isFullScreen}
                  currentTheme={currentTheme}
                  acknowledgeNotification={acknowledgeNotification}
                />
              )}

              {selectedSegment === "table" && (
                <TableSection
                  templates={templates}
                  templateStats={templateStats}
                  isFullScreen={isFullScreen}
                  currentTheme={currentTheme}
                />
              )}

              {showEmpty && (
                <div className="flex flex-wrap items-center justify-center text-white h-[100%]">
                  <div className="px-8 py-8">
                    <Result
                      title={<Title />}
                      subTitle={<SubTitle />}
                      status="404"
                    />
                  </div>
                </div>
              )}
              {!get(filter, "company") && (
                <div className="flex flex-wrap items-center justify-center text-white h-[100%]">
                  <div className="px-8 py-8">
                    <Result
                      title={<Title text={"Company not Selected"} />}
                      subTitle={<SubTitle />}
                      status="404"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <OfflineCard />
      </div>
    </Spin>
  );
}

export default AndonDashboard;
